// =============================================
// TableDetailViewV2 - New table detail view with draft/sent items
// =============================================
// This component displays a table's details with separate sections:
// - Draft items (editable, not yet sent)
// - Sent items (locked, in kitchen)
// - Button to load drafts into cart
// - Professional payment modal with multi-method support
// =============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import cartService from '../services/cartService';
import PaymentModal from './PaymentModal';
import {
  ArrowLeft, Clock, ChefHat, Utensils, ShoppingCart, 
  CreditCard, Trash2, AlertCircle, CheckCircle,
  Loader2, ExternalLink, Users, FileText, Sparkles, AlertTriangle,
  MessageSquare, Send, Wine
} from 'lucide-react';

// Status badge colors and labels
const STATUS_CONFIG = {
  'libre': { color: 'bg-green-100 text-green-800', label: 'Libre', icon: CheckCircle },
  'brouillon': { color: 'bg-amber-100 text-amber-800', label: 'Brouillon', icon: FileText },
  'occupee': { color: 'bg-orange-100 text-orange-800', label: 'Occupée', icon: Users },
  'en_preparation': { color: 'bg-blue-100 text-blue-800', label: 'En préparation', icon: ChefHat },
  'prete': { color: 'bg-purple-100 text-purple-800', label: 'Prête', icon: CheckCircle },
  'en_attente_paiement': { color: 'bg-red-100 text-red-800', label: 'En attente paiement', icon: CreditCard },
  'en_cours_de_paiement': { color: 'bg-indigo-100 text-indigo-800', label: 'En cours de paiement', icon: CreditCard },
  'a_debarrasser': { color: 'bg-yellow-100 text-yellow-800', label: 'À débarrasser', icon: Sparkles },
};

// Item status colors
const ITEM_STATUS_CONFIG = {
  'en_attente': { color: 'bg-yellow-100 text-yellow-700', label: 'En attente' },
  'en_preparation': { color: 'bg-orange-100 text-orange-700', label: 'En préparation' },
  'pret': { color: 'bg-green-100 text-green-700', label: 'Prêt' },
  'servi': { color: 'bg-blue-100 text-blue-700', label: 'Servi' },
};

// Format price
const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price) + ' F';
};

// Calculate elapsed time
const calculateElapsedTime = (startTime) => {
  if (!startTime) return null;
  const start = new Date(startTime);
  const now = new Date();
  const diff = Math.floor((now - start) / 1000);
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  if (hours > 0) return `${hours}h${minutes.toString().padStart(2, '0')}`;
  return `${minutes} min`;
};

const TableDetailViewV2 = ({ 
  table, 
  open, 
  onClose, 
  onUpdate,
  currentUser 
}) => {
  const navigate = useNavigate();
  const [tableDetails, setTableDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [kitchenMessage, setKitchenMessage] = useState('');
  const [messageDestination, setMessageDestination] = useState('cuisine'); // 'cuisine' or 'bar'
  
  // États pour le dialog d'annulation avec motif
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComment, setCancelComment] = useState('');
  const [sendToKitchen, setSendToKitchen] = useState(true);
  
  // Motifs d'annulation prédéfinis
  const CANCEL_REASONS = [
    { id: 'erreur_saisie', label: 'Erreur de saisie' },
    { id: 'client_change_avis', label: 'Client a changé d\'avis' },
    { id: 'produit_indisponible', label: 'Produit indisponible' },
    { id: 'doublon_commande', label: 'Doublon de commande' },
    { id: 'temps_attente', label: 'Temps d\'attente trop long' },
    { id: 'autre', label: 'Autre (préciser ci-dessous)' }
  ];

  const isAdmin = currentUser?.role === 'ADMIN';
  const isManager = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(currentUser?.role);
  const isOwner = table?.waiter_id === currentUser?.id;
  const canManage = isManager || isOwner;

  // Fetch table details
  const fetchTableDetails = useCallback(async () => {
    if (!table?.id) return;
    
    setLoading(true);
    try {
      const details = await cartService.getTableDetails(table.id);
      setTableDetails(details);
    } catch (error) {
      console.error('Error fetching table details:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [table?.id]);

  // Load on mount and when table changes
  useEffect(() => {
    if (open && table?.id) {
      fetchTableDetails();
    }
  }, [open, table?.id, fetchTableDetails]);

  // Timer for elapsed time
  useEffect(() => {
    const startTime = tableDetails?.opened_at || tableDetails?.updated_at;
    if (!startTime || tableDetails?.status === 'libre') {
      setElapsedTime(null);
      return;
    }
    
    setElapsedTime(calculateElapsedTime(startTime));
    const interval = setInterval(() => {
      setElapsedTime(calculateElapsedTime(startTime));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [tableDetails?.opened_at, tableDetails?.updated_at, tableDetails?.status]);

  // Open in cart (load draft items)
  const handleOpenInCart = async () => {
    if (!table?.id) return;
    
    setActionLoading(true);
    try {
      await cartService.loadDraftsFromTable(table.id);
      toast.success('Brouillons chargés dans le panier');
      onClose();
      navigate('/pos');
    } catch (error) {
      console.error('Error loading drafts:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setActionLoading(false);
    }
  };

  // Navigate to POS with table context
  // IMPORTANT: Utilise table_id comme paramètre standard pour la caisse
  const handleAddProducts = () => {
    console.log('[TableDetail] Adding products to table:', table?.number, table?.id);
    onClose();
    // Naviguer vers la caisse avec l'ID de la table
    // La caisse chargera automatiquement la commande existante
    navigate(`/pos?table_id=${table.id}`);
  };
  
  // Nouvelle commande = table vide, forcer le vidage complet du panier
  const handleNewOrder = () => {
    console.log('[TableDetail] Starting NEW order for table:', table?.number, table?.id);
    onClose();
    // force_clear=true indique au POS de vider le panier même si la table était déjà sélectionnée
    navigate(`/pos?table_id=${table.id}&force_clear=true`);
  };

  // Release table
  const handleReleaseTable = async () => {
    if (!table?.id) return;
    
    setActionLoading(true);
    try {
      await cartService.releaseTable(table.id);
      toast.success('Table libérée');
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error releasing table:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel/Release table (admin only - can close ANY table)
  // Ouvre le dialog de confirmation avec motif
  const openCancelDialog = () => {
    if (!table?.id || !isAdmin) return;
    setCancelReason('');
    setCancelComment('');
    setSendToKitchen(true);
    setShowCancelDialog(true);
  };
  
  // ANNULATION COMPLÈTE avec motif et notification cuisine
  const handleCancelOrder = async () => {
    if (!table?.id || !isAdmin) return;
    
    // Vérifier qu'un motif est sélectionné
    if (!cancelReason) {
      toast.error('Veuillez sélectionner un motif d\'annulation');
      return;
    }
    
    // Si "autre" est sélectionné, le commentaire est obligatoire
    if (cancelReason === 'autre' && !cancelComment.trim()) {
      toast.error('Veuillez préciser le motif d\'annulation');
      return;
    }
    
    setActionLoading(true);
    
    try {
      const reasonLabel = CANCEL_REASONS.find(r => r.id === cancelReason)?.label || cancelReason;
      const fullReason = cancelComment.trim() 
        ? `${reasonLabel} - ${cancelComment.trim()}`
        : reasonLabel;
      
      // 1. Envoyer le message en cuisine si l'option est cochée
      if (sendToKitchen && (sentItems.length > 0 || draftItems.length > 0)) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL || '';
          const token = localStorage.getItem('token');
          
          // Format du message pour la cuisine
          const kitchenNotification = `🚫 COMMANDE ANNULÉE - Table ${table.number}\n━━━━━━━━━━━━━━━━━━━━━━━━\nMotif: ${reasonLabel}${cancelComment.trim() ? `\nCommentaire: ${cancelComment.trim()}` : ''}\nAnnulé par: ${currentUser?.name || 'Admin'}\nHeure: ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
          
          await fetch(`${API_URL}/api/tables/kitchen-messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              table_id: table.id,
              message: kitchenNotification,
              destination: 'cuisine',
              type: 'cancellation',
              priority: 'high'
            })
          });
        } catch (msgError) {
          console.error('Error sending kitchen notification:', msgError);
          // Continue même si l'envoi du message échoue
        }
      }
      
      // 2. Annuler la commande via l'API
      const result = await cartService.cancelTableOrder(table.id, fullReason);
      
      // 3. Message de succès détaillé
      const itemCount = (sentItems?.length || 0) + (draftItems?.length || 0);
      if (itemCount > 0) {
        toast.success(`Table ${table.number} annulée - ${itemCount} article(s) supprimé(s)`, {
          description: `Motif: ${reasonLabel}`
        });
      } else {
        toast.success(`Table ${table.number} libérée`);
      }
      
      // 4. Fermer les dialogs et rafraîchir
      setShowCancelDialog(false);
      onClose();
      onUpdate?.();
      
    } catch (error) {
      console.error('Error canceling/releasing table:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setActionLoading(false);
    }
  };

  // Débarrasser la table (après paiement)
  const handleClearTable = async () => {
    if (!table?.id) return;
    
    setActionLoading(true);
    try {
      await cartService.clearTable(table.id);
      toast.success(`Table ${table?.number} libérée`);
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error clearing table:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la libération');
    } finally {
      setActionLoading(false);
    }
  };

  // Envoyer un message à la cuisine ou au bar
  const handleSendMessage = async () => {
    if (!table?.id || !kitchenMessage.trim()) {
      toast.error('Veuillez saisir un message');
      return;
    }
    
    setActionLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      
      const endpoint = messageDestination === 'bar' 
        ? `${API_BASE}/api/bar/messages`
        : `${API_BASE}/api/tables/kitchen-messages`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          table_id: table.id,
          table_number: table.number,
          message: kitchenMessage.trim(),
          source: 'table'
        })
      });
      
      if (!response.ok) throw new Error('Erreur lors de l\'envoi');
      
      const destinationLabel = messageDestination === 'bar' ? 'bar' : 'cuisine';
      toast.success(`Message envoyé ${messageDestination === 'bar' ? 'au' : 'à la'} ${destinationLabel}`);
      setShowMessageDialog(false);
      setKitchenMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setActionLoading(false);
    }
  };

  // Callback après paiement réussi
  const handlePaymentSuccess = (paymentData) => {
    setShowPayment(false);
    toast.success(`Table ${table?.number} encaissée avec succès!`);
    onUpdate?.();
    onClose();
  };

  // Callback pour fermeture/annulation du modal de paiement
  const handlePaymentClose = async () => {
    // Si la table est en cours de paiement, annuler la session
    if (tableDetails?.status === 'en_cours_de_paiement') {
      try {
        await cartService.cancelPaymentSession(table.id);
        // Recharger les détails de la table
        await fetchTableDetails();
      } catch (error) {
        console.error('Error canceling payment session:', error);
      }
    }
    setShowPayment(false);
  };

  // === RÈGLE 1: Clic sur "Encaisser" = Mettre statut "EN_PAIEMENT" PUIS ouvrir modal ===
  const handleStartPayment = async () => {
    if (!table?.id) return;
    
    // Vérifier qu'il y a des items à payer
    if (draftItems.length === 0 && sentItems.length === 0) {
      toast.error('Aucun article à encaisser');
      return;
    }
    
    // Si déjà en cours de paiement, rouvrir simplement le modal
    if (tableDetails?.status === 'en_cours_de_paiement') {
      setShowPayment(true);
      return;
    }
    
    setActionLoading(true);
    try {
      // 1. Appel API pour mettre la table en statut "en_cours_de_paiement"
      const response = await cartService.startPaymentSession(table.id);
      
      // 2. Mettre à jour les détails locaux avec le nouveau statut
      setTableDetails(prev => ({
        ...prev,
        status: 'en_cours_de_paiement',
        payment_session: response.payment_session
      }));
      
      // 3. Ouvrir le modal de paiement SEULEMENT après confirmation du statut
      setShowPayment(true);
      
    } catch (error) {
      console.error('Error starting payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture de l\'encaissement');
    } finally {
      setActionLoading(false);
    }
  };

  // Gestion de la fermeture avec confirmation
  const handleCloseRequest = () => {
    // Vérifier s'il y a des modifications non envoyées (draft items)
    if (draftItems.length > 0) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  // Get status config
  const status = tableDetails?.status || table?.status || 'libre';
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG['libre'];
  const StatusIcon = statusConfig.icon;

  // Calculate totals
  const draftItems = tableDetails?.draft_items || [];
  const sentItems = tableDetails?.sent_items || [];
  const draftTotal = draftItems.reduce((sum, item) => sum + (item.line_total || item.unit_price * item.quantity), 0);
  const sentTotal = sentItems.reduce((sum, item) => sum + (item.line_total || item.unit_price * item.quantity), 0);
  const grandTotal = tableDetails?.total || (draftTotal + sentTotal);

  return (
    <>
    <Sheet open={open} onOpenChange={handleCloseRequest}>
      <SheetContent side="right" className="w-full sm:max-w-md lg:max-w-xl p-0 overflow-hidden">
        {/* Header - Info table complète */}
        <SheetHeader className="p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <div className="flex items-center justify-between">
            {/* Bouton Retour à gauche */}
            <Button 
              variant="ghost" 
              onClick={handleCloseRequest} 
              className="h-9 px-2 sm:px-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="text-sm">Retour</span>
            </Button>
            
            {/* Info table au centre/droite */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="text-right">
                <SheetTitle className="text-base sm:text-lg">
                  Table {table?.number}
                  {table?.zone && (
                    <span className="text-xs sm:text-sm font-normal text-muted-foreground ml-1 sm:ml-2 capitalize">
                      ({table.zone})
                    </span>
                  )}
                </SheetTitle>
                <div className="flex items-center justify-end gap-1 sm:gap-2 mt-1 flex-wrap">
                  <Badge className={`${statusConfig.color} gap-1 text-xs`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig.label}
                  </Badge>
                  {elapsedTime && (
                    <Badge variant="outline" className="gap-1 text-xs">
                      <Clock className="w-3 h-3" />
                      {elapsedTime}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                <span className="text-xl sm:text-2xl font-bold text-blue-600">
                  {table?.number || '?'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Infos supplémentaires : Serveur et heure d'ouverture */}
          {(tableDetails?.waiter_name || tableDetails?.opened_at) && (
            <div className="flex items-center justify-between text-xs text-gray-600 mt-2 pt-2 border-t border-blue-100">
              {tableDetails?.waiter_name && (
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  <span>Serveur: <strong>{tableDetails.waiter_name}</strong></span>
                </div>
              )}
              {tableDetails?.opened_at && (
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>Ouvert à: <strong>{new Date(tableDetails.opened_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</strong></span>
                </div>
              )}
            </div>
          )}
        </SheetHeader>

        {/* Content - Responsive */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              {/* Draft Items Section */}
              {draftItems.length > 0 && (
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold flex items-center gap-2 text-yellow-800">
                      <FileText className="w-4 h-4" />
                      Brouillon ({draftItems.length})
                    </h3>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleOpenInCart}
                      disabled={actionLoading}
                      className="border-yellow-400 text-yellow-700 hover:bg-yellow-100"
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      Ouvrir dans le panier
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {draftItems.map((item, idx) => (
                      <div key={item.id || idx} className="flex items-center justify-between py-2 border-b border-yellow-200 last:border-0">
                        <div>
                          <p className="font-medium text-sm">{item.product_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} × {formatPrice(item.unit_price)}
                          </p>
                        </div>
                        <span className="font-semibold text-sm">
                          {formatPrice(item.line_total || item.unit_price * item.quantity)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-3 pt-2 border-t border-yellow-300">
                    <span className="text-sm text-yellow-700">Sous-total brouillon</span>
                    <span className="font-bold text-yellow-800">{formatPrice(draftTotal)}</span>
                  </div>
                </div>
              )}

              {/* Sent Items Section - Séparés par destination */}
              {sentItems.length > 0 && (() => {
                // Séparer items cuisine et bar
                const kitchenItems = sentItems.filter(item => item.destination !== 'bar');
                const barItems = sentItems.filter(item => item.destination === 'bar');
                const kitchenTotal = kitchenItems.reduce((sum, item) => sum + (item.line_total || item.unit_price * item.quantity), 0);
                const barTotal = barItems.reduce((sum, item) => sum + (item.line_total || item.unit_price * item.quantity), 0);
                
                return (
                  <div className="space-y-3">
                    {/* Items Cuisine */}
                    {kitchenItems.length > 0 && (
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <h3 className="font-semibold flex items-center gap-2 text-blue-800 mb-3">
                          <ChefHat className="w-4 h-4" />
                          Cuisine ({kitchenItems.length})
                        </h3>
                        <div className="space-y-2">
                          {kitchenItems.map((item, idx) => (
                            <div key={item.id || `k-${idx}`} className="flex items-center justify-between py-2 border-b border-blue-200 last:border-0">
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{item.product_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {item.quantity} × {formatPrice(item.unit_price)}
                                  </p>
                                </div>
                                {item.status && ITEM_STATUS_CONFIG[item.status] && (
                                  <Badge className={`${ITEM_STATUS_CONFIG[item.status].color} text-xs`}>
                                    {ITEM_STATUS_CONFIG[item.status].label}
                                  </Badge>
                                )}
                              </div>
                              <span className="font-semibold text-sm ml-2">
                                {formatPrice(item.line_total || item.unit_price * item.quantity)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between mt-3 pt-2 border-t border-blue-300">
                          <span className="text-sm text-blue-700">Sous-total cuisine</span>
                          <span className="font-bold text-blue-800">{formatPrice(kitchenTotal)}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Items Bar */}
                    {barItems.length > 0 && (
                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                        <h3 className="font-semibold flex items-center gap-2 text-purple-800 mb-3">
                          <Wine className="w-4 h-4" />
                          Bar ({barItems.length})
                        </h3>
                        <div className="space-y-2">
                          {barItems.map((item, idx) => (
                            <div key={item.id || `b-${idx}`} className="flex items-center justify-between py-2 border-b border-purple-200 last:border-0">
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{item.product_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {item.quantity} × {formatPrice(item.unit_price)}
                                  </p>
                                </div>
                                {item.status && ITEM_STATUS_CONFIG[item.status] && (
                                  <Badge className={`${ITEM_STATUS_CONFIG[item.status].color} text-xs`}>
                                    {ITEM_STATUS_CONFIG[item.status].label}
                                  </Badge>
                                )}
                              </div>
                              <span className="font-semibold text-sm ml-2">
                                {formatPrice(item.line_total || item.unit_price * item.quantity)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between mt-3 pt-2 border-t border-purple-300">
                          <span className="text-sm text-purple-700">Sous-total bar</span>
                          <span className="font-bold text-purple-800">{formatPrice(barTotal)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Empty state */}
              {draftItems.length === 0 && sentItems.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Utensils className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Table vide</p>
                  <p className="text-sm">Aucune commande en cours</p>
                </div>
              )}

              {/* Total */}
              {(draftItems.length > 0 || sentItems.length > 0) && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-green-800">TOTAL</span>
                    <span className="text-2xl font-bold text-green-700">{formatPrice(grandTotal)}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions - Interface claire et simple */}
        <div className="border-t bg-gray-50 p-4 space-y-3">
          
          {/* ===== CAS 1: Table à débarrasser ===== */}
          {status === 'a_debarrasser' && (
            <div className="space-y-2 sm:space-y-3">
              <div className="text-center text-xs sm:text-sm text-gray-500 mb-2">
                Table payée - Prête à être libérée
              </div>
              <Button 
                onClick={handleClearTable}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white h-12 sm:h-14 text-base sm:text-lg font-semibold"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                )}
                Débarrasser la table
              </Button>
            </div>
          )}

          {/* ===== CAS 2: Table libre ===== */}
          {status === 'libre' && (
            <div className="text-center py-3 sm:py-4">
              <p className="text-gray-500 mb-2 sm:mb-3 text-sm">Table disponible</p>
              <Button 
                onClick={handleNewOrder}
                className="bg-blue-600 hover:bg-blue-700 h-10 sm:h-11"
                disabled={actionLoading}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Nouvelle commande
              </Button>
            </div>
          )}

          {/* ===== CAS 3: Table occupée ou en cours ===== */}
          {status !== 'a_debarrasser' && status !== 'libre' && (
            <>
              {/* Boutons principaux - Responsive */}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  onClick={handleAddProducts}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 h-11 sm:h-12 text-sm sm:text-base"
                  disabled={actionLoading}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ajouter produits
                </Button>
                
                {(sentItems.length > 0 || draftItems.length > 0) && (
                  <Button 
                    onClick={handleStartPayment}
                    className={`flex-1 h-11 sm:h-12 text-sm sm:text-base ${
                      tableDetails?.status === 'en_cours_de_paiement' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    {tableDetails?.status === 'en_cours_de_paiement' ? 'Reprendre paiement' : 'Encaisser'}
                  </Button>
                )}
              </div>

              {/* Bouton Annuler/Libérer - Admin SEUL peut fermer les tables */}
              {isAdmin && tableDetails?.status !== 'libre' && (
                <Button 
                  variant="outline"
                  onClick={openCancelDialog}
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 h-10 text-sm"
                  disabled={actionLoading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {(sentItems.length > 0 || draftItems.length > 0) ? 'Annuler la commande' : 'Libérer la table'}
                </Button>
              )}

              {/* Boutons Messages Cuisine/Bar - Pour tous les utilisateurs sur tables occupées */}
              {tableDetails?.status !== 'libre' && (
                <div className="flex gap-2 w-full">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setMessageDestination('cuisine');
                      setShowMessageDialog(true);
                    }}
                    className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50 h-10 text-sm"
                    disabled={actionLoading}
                  >
                    <ChefHat className="w-4 h-4 mr-1" />
                    Cuisine
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setMessageDestination('bar');
                      setShowMessageDialog(true);
                    }}
                    className="flex-1 border-indigo-300 text-indigo-600 hover:bg-indigo-50 h-10 text-sm"
                    disabled={actionLoading}
                  >
                    <Wine className="w-4 h-4 mr-1" />
                    Bar
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>

    {/* Modal de paiement professionnel */}
    <PaymentModal
      open={showPayment}
      onClose={handlePaymentClose}
      table={table}
      tableDetails={tableDetails}
      onPaymentSuccess={handlePaymentSuccess}
    />

    {/* Dialog de confirmation pour fermeture avec modifications non envoyées */}
    <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Modifications non envoyées
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-gray-600">
            Il y a <strong>{draftItems.length}</strong> article(s) non envoyé(s) en cuisine.
            Les modifications non envoyées seront perdues si vous fermez maintenant.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowCloseConfirm(false)}
          >
            Annuler
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleConfirmClose}
          >
            Confirmer
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Dialog pour envoyer un message à la cuisine ou au bar */}
    <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${messageDestination === 'bar' ? 'text-indigo-600' : 'text-orange-600'}`}>
            {messageDestination === 'bar' ? (
              <>
                <Wine className="w-5 h-5" />
                Message pour le Bar
              </>
            ) : (
              <>
                <ChefHat className="w-5 h-5" />
                Message pour la Cuisine
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-3">
          <div className={`rounded-lg p-3 text-sm ${messageDestination === 'bar' ? 'bg-indigo-50' : 'bg-orange-50'}`}>
            <p className={`font-medium ${messageDestination === 'bar' ? 'text-indigo-700' : 'text-orange-700'}`}>Table {table?.number}</p>
            <p className={`text-xs ${messageDestination === 'bar' ? 'text-indigo-600' : 'text-orange-600'}`}>
              Ce message sera visible sur l'écran {messageDestination === 'bar' ? 'bar' : 'cuisine'}
            </p>
          </div>
          <Textarea
            placeholder={messageDestination === 'bar' 
              ? "Ex: Cocktail sans alcool, Glaçons supplémentaires..." 
              : "Ex: Client allergique aux noix, Cuisson bien cuite, Sans oignon..."}
            value={kitchenMessage}
            onChange={(e) => setKitchenMessage(e.target.value)}
            className="min-h-[100px] resize-none"
            maxLength={200}
          />
          <p className="text-xs text-gray-400 text-right">{kitchenMessage.length}/200</p>
        </div>
        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              setShowMessageDialog(false);
              setKitchenMessage('');
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleSendMessage}
            disabled={!kitchenMessage.trim() || actionLoading}
            className={messageDestination === 'bar' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-orange-600 hover:bg-orange-700'}
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    
    {/* ===== DIALOG D'ANNULATION AVEC MOTIF - RESPONSIVE MOBILE ===== */}
    <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <DialogContent className="
        sm:max-w-md 
        max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto 
        max-sm:translate-y-0 max-sm:translate-x-0
        max-sm:rounded-b-none max-sm:rounded-t-2xl
        max-sm:w-full max-sm:max-w-full
        max-sm:max-h-[85vh]
        max-sm:animate-in max-sm:slide-in-from-bottom
        max-sm:p-0
        overflow-hidden
      ">
        {/* Header fixe */}
        <DialogHeader className="max-sm:sticky max-sm:top-0 max-sm:z-10 max-sm:bg-white max-sm:border-b max-sm:px-4 max-sm:py-3 sm:pb-2">
          {/* Indicateur de drag mobile */}
          <div className="sm:hidden w-12 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
          <DialogTitle className="flex items-center gap-2 text-red-600 max-sm:text-base">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="truncate">Annuler - Table {table?.number}</span>
          </DialogTitle>
        </DialogHeader>
        
        {/* Contenu scrollable */}
        <div className="
          space-y-3 py-2 
          max-sm:px-4 max-sm:py-3
          max-sm:overflow-y-auto max-sm:max-h-[calc(85vh-140px)]
          max-sm:overscroll-contain
        ">
          {/* Résumé de la commande */}
          {(sentItems.length > 0 || draftItems.length > 0) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-sm:p-2.5">
              <p className="text-sm font-medium text-red-800 max-sm:text-xs">
                ⚠️ {sentItems.length + draftItems.length} article(s) seront supprimés
              </p>
              <p className="text-xs text-red-600 mt-1">
                Total: {formatPrice((sentItems.concat(draftItems)).reduce((sum, item) => sum + (item.price * item.quantity), 0))}
              </p>
            </div>
          )}
          
          {/* Sélection du motif */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 max-sm:text-xs max-sm:mb-1.5">
              Motif d'annulation <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 max-sm:space-y-1.5">
              {CANCEL_REASONS.map((reason) => (
                <label 
                  key={reason.id}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                    max-sm:p-2.5 max-sm:gap-2.5 max-sm:min-h-[44px]
                    active:scale-[0.98] touch-manipulation
                    ${cancelReason === reason.id 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="cancelReason"
                    value={reason.id}
                    checked={cancelReason === reason.id}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="w-5 h-5 max-sm:w-4 max-sm:h-4 text-red-600 focus:ring-red-500 flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700 max-sm:text-xs">{reason.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Commentaire libre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 max-sm:text-xs max-sm:mb-1.5">
              Commentaire {cancelReason === 'autre' && <span className="text-red-500">*</span>}
            </label>
            <Textarea
              placeholder="Précisions supplémentaires..."
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
              className="min-h-[70px] max-sm:min-h-[60px] resize-none text-sm max-sm:text-xs w-full"
              maxLength={200}
            />
            <p className="text-xs text-gray-400 text-right mt-1 max-sm:text-[10px]">{cancelComment.length}/200</p>
          </div>
          
          {/* Option d'envoi en cuisine */}
          {(sentItems.length > 0 || draftItems.length > 0) && (
            <label className="
              flex items-start gap-3 p-3 rounded-lg border border-orange-200 bg-orange-50 cursor-pointer
              max-sm:p-2.5 max-sm:gap-2.5 max-sm:min-h-[44px]
              active:scale-[0.98] touch-manipulation
            ">
              <input
                type="checkbox"
                checked={sendToKitchen}
                onChange={(e) => setSendToKitchen(e.target.checked)}
                className="w-5 h-5 max-sm:w-4 max-sm:h-4 mt-0.5 text-orange-600 focus:ring-orange-500 rounded flex-shrink-0"
              />
              <div className="min-w-0">
                <span className="text-sm font-medium text-orange-800 max-sm:text-xs">Notifier la cuisine</span>
                <p className="text-xs text-orange-600 mt-0.5 max-sm:text-[10px]">
                  Un message d'annulation sera envoyé à l'écran cuisine
                </p>
              </div>
            </label>
          )}
          
          {/* Avertissement */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-sm:p-2.5">
            <p className="text-xs text-yellow-800 max-sm:text-[10px]">
              <strong>⚠️ Action irréversible :</strong> Cette action annulera définitivement la commande et libérera la table.
            </p>
          </div>
        </div>
        
        {/* Footer fixe en bas */}
        <DialogFooter className="
          gap-2 sm:gap-0 
          max-sm:sticky max-sm:bottom-0 max-sm:z-10 
          max-sm:bg-white max-sm:border-t 
          max-sm:px-4 max-sm:py-3 max-sm:pb-[calc(0.75rem+env(safe-area-inset-bottom))]
          max-sm:flex-row max-sm:gap-3
        ">
          <Button 
            variant="outline" 
            onClick={() => setShowCancelDialog(false)}
            disabled={actionLoading}
            className="max-sm:flex-1 max-sm:h-11 max-sm:text-sm"
          >
            Retour
          </Button>
          <Button 
            onClick={handleCancelOrder}
            disabled={!cancelReason || (cancelReason === 'autre' && !cancelComment.trim()) || actionLoading}
            className="bg-red-600 hover:bg-red-700 text-white max-sm:flex-1 max-sm:h-11 max-sm:text-sm"
          >
            {actionLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            <span className="max-sm:hidden">Confirmer l'annulation</span>
            <span className="sm:hidden">Confirmer</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default TableDetailViewV2;
